/**
 * CK | Kaching Bridge – Kaching Bundles Edition (v3 – Upsell-Sync + Preis-Update)
 *
 * Verbindet das CK-Karten-Design mit dem Kaching Bundles Widget.
 * Das Kaching Widget ist per CSS versteckt (ck-product-custom-variants.css).
 *
 * Funktionsweise:
 * - Kaching injiziert hidden inputs ins Produktformular:
 *   properties[__kaching_bundles] = {"deal":"...","main":true,"bid":"..."}
 *   quantity = N
 * - Beim Klick auf eine CK-Karte wird die entsprechende Kaching Deal-Bar
 *   per Radio-Button-Klick ausgewaehlt → Kaching aktualisiert die hidden inputs
 * - Der CK-Kaufbutton-Klick wird NICHT blockiert – das Formular submittet
 *   mit den richtigen Kaching-Properties
 *
 * Upsell-Sync:
 * - Kaching rendert Upsell-Elemente (.kaching-bundles__upsell) pro Deal-Bar
 * - Diese sind per CSS versteckt; CK-Upsell-Karten werden stattdessen
 *   dynamisch aus den Kaching-Daten gerendert (CK-Design)
 * - Upsells sind standardmaessig NICHT ausgewaehlt (unabhaengig von Kaching preselected)
 * - Klick auf CK-Upsell-Karte toggelt die Kaching Checkbox
 * - Kaching uebernimmt den Cart-Add inkl. Discount automatisch
 *
 * Gesamtpreis-Update:
 * - Beim Toggle einer Upsell-Karte wird der Gesamtpreis im Buy-Button
 *   aktualisiert (Hauptprodukt + ggf. Upsell-Preis)
 *
 * Ablauf:
 * 1. Beim Klick auf eine CK-Karte: Synchronisiere die Kaching Deal-Bar
 * 2. Beim Klick auf den CK-Kaufbutton: Sicherheits-Sync, dann durchlassen
 * 3. Upsell-Karten aus Kaching DOM bauen und bei Kartenwechsel aktualisieren
 * 4. Gesamtpreis im Buy-Button bei Upsell-Toggle aktualisieren
 */
(() => {
  'use strict';

  /* ── Helfer ──────────────────────────────────────────────────────────── */

  const escHtml = (str) =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const escAttr = (str) =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;');

  /**
   * Parst einen Euro-Preisstring zu Cent-Wert (Integer).
   * Beispiele: "€24,99" → 2499, "€59,00" → 5900, "€99,96" → 9996
   */
  const parsePriceCents = (str) => {
    if (!str) return 0;
    const cleaned = str.replace(/[^0-9,\.]/g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : Math.round(num * 100);
  };

  /**
   * Formatiert Cent-Wert als Euro-String.
   * Beispiel: 2499 → "€24,99"
   */
  const formatPrice = (cents) => {
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return `\u20AC${euros}`;
  };

  /**
   * Findet das Kaching Bundles Block Element.
   */
  const getKachingBlock = () => document.querySelector('kaching-bundles-block');

  /**
   * Waehlt eine Kaching Deal-Bar per Radio-Button-Klick aus.
   * @param {string} dealBarId - Die data-deal-bar-id der gewuenschten Bar
   * @returns {boolean} true wenn erfolgreich
   */
  const selectKachingDealBar = (dealBarId) => {
    if (!dealBarId) return false;
    const kachingBlock = getKachingBlock();
    if (!kachingBlock) return false;

    const radio = kachingBlock.querySelector(
      `input[type="radio"][value="${CSS.escape(dealBarId)}"]`
    );
    if (radio) {
      radio.click();
      return true;
    }
    return false;
  };

  /**
   * Liest die Deal-Bar-ID der aktuell ausgewaehlten CK-Karte.
   * Gibt die ID der ersten Karte zurueck wenn keine ausgewaehlt ist.
   */
  const getActiveDealBarId = (variantsRoot) => {
    if (!variantsRoot) return null;
    const activeCard = variantsRoot.querySelector(
      '[data-ck-variant-card].is-selected'
    );
    if (activeCard) {
      return activeCard.getAttribute('data-ck-deal-bar-id');
    }
    // Fallback: erste Karte
    const firstCard = variantsRoot.querySelector('[data-ck-variant-card]');
    return firstCard ? firstCard.getAttribute('data-ck-deal-bar-id') : null;
  };

  /* ── Upsell-Helfer ──────────────────────────────────────────────────── */

  /**
   * Liest die Kaching Produkt-Daten aus den Script-Tags im Head.
   * @returns {Map<string, object>} Map von Produkt-GID zu Produkt-Daten
   */
  const getKachingProducts = () => {
    const map = new Map();
    document.querySelectorAll('script.kaching-bundles-product').forEach((s) => {
      try {
        const d = JSON.parse(s.textContent);
        if (d && d.id) {
          map.set(String(d.id), d);
        }
      } catch (e) { /* ignore */ }
    });
    return map;
  };

  /**
   * Liest die Deal-Block-Settings aus dem Kaching Script-Tag.
   * @returns {object|null}
   */
  const getDealBlockSettings = () => {
    const s = document.querySelector('script.kaching-bundles-deal-block-settings');
    if (!s) return null;
    try {
      return JSON.parse(s.textContent);
    } catch (e) {
      return null;
    }
  };

  /**
   * Extrahiert Upsell-Daten fuer eine bestimmte Deal-Bar aus dem Kaching DOM.
   * WICHTIG: isSelected wird immer auf false gesetzt (CK-Default = deselektiert).
   * @param {string} dealBarId
   * @returns {Array<{upsellId, title, desc, imgSrc, price, fullPrice, savingsPercent, isSelected}>}
   */
  const getUpsellsForDealBar = (dealBarId) => {
    const kachingBlock = getKachingBlock();
    if (!kachingBlock || !dealBarId) return [];

    const settings = getDealBlockSettings();
    if (!settings || !settings.dealBars) return [];

    const products = getKachingProducts();

    const dealBar = settings.dealBars.find((b) => b.id === dealBarId);
    if (!dealBar || !dealBar.upsells || dealBar.upsells.length === 0) return [];

    return dealBar.upsells.map((upsell) => {
      // Finde das DOM-Element fuer diesen Upsell
      const upsellEl = kachingBlock.querySelector(
        `.kaching-bundles__upsell[data-upsell-id="${CSS.escape(upsell.id)}"]`
      );

      // Preis aus DOM lesen (Kaching berechnet den Rabatt)
      const priceText = upsellEl
        ? upsellEl.querySelector('.kaching-bundles__upsell__price')?.textContent?.trim() || ''
        : '';
      const fullPriceText = upsellEl
        ? upsellEl.querySelector('.kaching-bundles__upsell__full-price')?.textContent?.trim() || ''
        : '';

      // Produkt-Daten aus Script-Tag
      const productGidNum = upsell.productGID?.replace('gid://shopify/Product/', '');
      const product = products.get(productGidNum);

      const title = product?.title || '';
      const desc = product?.metafields?.custom?.ck_subtitle?.value || '';
      const handle = product?.handle || '';

      // Bild: aus DOM oder aus Produkt-Daten
      const imgSrc = upsellEl
        ? upsellEl.querySelector('.kaching-bundles__upsell__image')?.src || ''
        : (product?.featured_image || '');

      // Savings berechnen
      let savingsPercent = 0;
      if (upsell.discountType === 'percentage' && upsell.discountValue) {
        savingsPercent = parseInt(upsell.discountValue, 10);
      }

      return {
        upsellId: upsell.id,
        title,
        desc: desc ? String(desc).substring(0, 90) : '',
        imgSrc,
        price: priceText,
        fullPrice: fullPriceText,
        savingsPercent,
        isSelected: false, // ← IMMER deselektiert beim Rendern
        handle,
      };
    });
  };

  /**
   * Rendert eine CK-Upsell-Karte als HTML-String.
   */
  const renderUpsellCard = (data) => {
    const selectedClass = data.isSelected ? ' is-selected' : '';
    const ariaPressedVal = data.isSelected ? 'true' : 'false';

    const imgHtml = data.imgSrc
      ? `<img src="${escAttr(data.imgSrc)}" alt="${escAttr(data.title)}" loading="lazy">`
      : '';

    const compareHtml = data.fullPrice && data.price && data.fullPrice !== data.price
      ? `<span class="ck-upsell-item__compare">statt <s>${escHtml(data.fullPrice)}</s></span>`
      : '';

    const savingsHtml = data.savingsPercent > 0
      ? `<span class="ck-upsell-item__savings">${data.savingsPercent}% Sparen</span>`
      : '';

    const descHtml = data.desc
      ? `<div class="ck-upsell-item__desc">${escHtml(data.desc)}</div>`
      : '';

    // Plus-Icon SVG
    const plusSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="var(--ck-accent-color)"><g clip-path="url(#ck-up)"><path d="M18.7328 8.87346H10.8454V0.98588C10.8454 0.441759 10.4036 0 9.85934 0C9.31522 0 8.87346 0.441759 8.87346 0.98588V8.87346H0.98588C0.441759 8.87346 0 9.31522 0 9.85934C0 10.4036 0.441759 10.8454 0.98588 10.8454H8.87346V18.7328C8.87346 19.2771 9.31522 19.7189 9.85934 19.7189C10.4036 19.7189 10.8454 19.2771 10.8454 18.7328V10.8454H18.7328C19.2771 10.8454 19.7189 10.4036 19.7189 9.85934C19.7189 9.31522 19.2771 8.87346 18.7328 8.87346Z" fill="var(--ck-accent-color)"/></g><defs><clipPath id="ck-up"><rect width="19.7188" height="19.7188" fill="var(--ck-accent-color)"/></clipPath></defs></svg>';

    // Check-Icon SVG
    const checkSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><g clip-path="url(#ck-uc)"><path fill-rule="evenodd" clip-rule="evenodd" d="M18.1462 3.56423C18.5159 3.88767 18.5494 4.45115 18.2206 4.81606L8.96517 15.089C8.63467 15.4558 8.06721 15.4792 7.70772 15.1408L2.35193 10.0988C2.01792 9.78431 1.98595 9.26035 2.27252 8.90215C2.58872 8.5069 3.17757 8.4525 3.55389 8.79097L7.70926 12.5287C8.07038 12.8535 8.62635 12.8244 8.9517 12.4638L16.9127 3.63705C17.2348 3.27986 17.7842 3.24748 18.1462 3.56423Z" fill="#fff"/></g><defs><clipPath id="ck-uc"><rect width="19.7188" height="19.7188" fill="#fff"/></clipPath></defs></svg>';

    return `
<button
  type="button"
  class="ck-upsell-item${selectedClass}"
  role="listitem"
  data-ck-upsell-item
  data-ck-upsell-id="${escAttr(data.upsellId)}"
  data-ck-upsell-price="${parsePriceCents(data.price)}"
  aria-pressed="${ariaPressedVal}"
>
  <div class="ck-upsell-item__media">
    ${imgHtml}
  </div>
  <div class="ck-upsell-item__content">
    <div class="ck-upsell-item__badge-row">
      <span class="ck-upsell-item__badge">Exklusiver Kombipreis</span>
    </div>
    <div class="ck-upsell-item__title">${escHtml(data.title)}</div>
    ${descHtml}
    <div class="ck-upsell-item__price-row">
      <span class="ck-upsell-item__price">${escHtml(data.price)}</span>
      ${compareHtml}
      ${savingsHtml}
    </div>
    <span class="ck-upsell-item__toggle" aria-hidden="true">
      <span class="ck-upsell-item__toggle-plus">${plusSvg}</span>
      <span class="ck-upsell-item__toggle-check">${checkSvg}</span>
    </span>
  </div>
</button>`;
  };

  /**
   * Toggelt einen Kaching Upsell Checkbox.
   * @param {string} upsellId
   */
  const toggleKachingUpsell = (upsellId) => {
    const kachingBlock = getKachingBlock();
    if (!kachingBlock) return;

    const upsellEl = kachingBlock.querySelector(
      `.kaching-bundles__upsell[data-upsell-id="${CSS.escape(upsellId)}"]`
    );
    if (!upsellEl) return;

    const checkbox = upsellEl.querySelector('.kaching-bundles__upsell__checkbox');
    if (checkbox) {
      checkbox.click();
    }
  };

  /**
   * Deselektiert einen Kaching Upsell Checkbox, falls er aktuell ausgewaehlt ist.
   * @param {string} upsellId
   */
  const deselectKachingUpsell = (upsellId) => {
    const kachingBlock = getKachingBlock();
    if (!kachingBlock) return;

    const upsellEl = kachingBlock.querySelector(
      `.kaching-bundles__upsell[data-upsell-id="${CSS.escape(upsellId)}"]`
    );
    if (!upsellEl) return;

    const isSelected = !!upsellEl.querySelector('.kaching-bundles__upsell__checkbox--selected');
    if (isSelected) {
      const checkbox = upsellEl.querySelector('.kaching-bundles__upsell__checkbox');
      if (checkbox) checkbox.click();
    }
  };

  /* ── Gesamtpreis-Update ────────────────────────────────────────────── */

  /**
   * Aktualisiert den Gesamtpreis im Buy-Button.
   * Gesamtpreis = Hauptprodukt-Preis + Summe aller ausgewaehlten Upsell-Preise.
   * @param {string|null} sectionId
   */
  const updateTotalPrice = (sectionId) => {
    const buyButtonRoot = sectionId
      ? document.querySelector(`[data-ck-buy-button][data-ck-section-id="${CSS.escape(sectionId)}"]`)
      : document.querySelector('[data-ck-buy-button]');
    if (!buyButtonRoot) return;

    const priceCurrent = buyButtonRoot.querySelector('[data-ck-buy-button-price-current]');
    if (!priceCurrent) return;

    // Hauptprodukt-Preis aus der aktuell ausgewaehlten CK-Karte lesen
    const variantsRoot = sectionId
      ? document.querySelector(`[data-ck-custom-variants][data-ck-section-id="${CSS.escape(sectionId)}"]`)
      : document.querySelector('[data-ck-custom-variants]');

    let mainPriceCents = 0;
    if (variantsRoot) {
      const activeCard = variantsRoot.querySelector('[data-ck-variant-card].is-selected');
      if (activeCard) {
        const priceEl = activeCard.querySelector('.ck-custom-variants__price-current');
        mainPriceCents = parsePriceCents(priceEl?.textContent);
      }
    }

    // Falls kein Preis aus Karte, den aktuell angezeigten Preis als Fallback nehmen
    if (mainPriceCents === 0) {
      mainPriceCents = parsePriceCents(priceCurrent.textContent);
    }

    // Upsell-Preise summieren (nur ausgewaehlte)
    let upsellTotalCents = 0;
    const grid = document.querySelector('[data-ck-upsell-grid]');
    if (grid) {
      grid.querySelectorAll('[data-ck-upsell-item].is-selected').forEach((btn) => {
        const priceCentsAttr = parseInt(btn.getAttribute('data-ck-upsell-price') || '0', 10);
        upsellTotalCents += priceCentsAttr;
      });
    }

    const totalCents = mainPriceCents + upsellTotalCents;
    priceCurrent.textContent = formatPrice(totalCents);
  };

  /* ── Upsell-Grid bauen / aktualisieren ──────────────────────────────── */

  /**
   * Baut die CK-Upsell-Karten fuer die aktuell ausgewaehlte Deal-Bar.
   * Upsells sind standardmaessig NICHT ausgewaehlt.
   * Kaching Checkboxen werden ebenfalls deselektiert.
   * @param {string} dealBarId
   * @param {string|null} sectionId - fuer Gesamtpreis-Update
   */
  const buildUpsellGrid = (dealBarId, sectionId) => {
    const grid = document.querySelector('[data-ck-upsell-grid]');
    if (!grid) return;

    const upsells = getUpsellsForDealBar(dealBarId);

    if (upsells.length === 0) {
      // Keine Upsells – Container verstecken
      const root = grid.closest('[data-ck-upsell-root]');
      if (root) root.style.display = 'none';
      grid.classList.remove('ck-upsell__items--loading');
      grid.innerHTML = '';
      return;
    }

    // Container sichtbar machen
    const root = grid.closest('[data-ck-upsell-root]');
    if (root) root.style.display = '';

    const cardsHtml = upsells.map((u) => renderUpsellCard(u)).join('');
    grid.classList.remove('ck-upsell__items--loading');
    grid.innerHTML = cardsHtml;

    // Kaching Checkboxen deselektieren (falls Kaching sie preselected hat)
    upsells.forEach((u) => {
      deselectKachingUpsell(u.upsellId);
    });

    // Klick-Handler auf CK-Upsell-Karten
    grid.querySelectorAll('[data-ck-upsell-item]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const uid = btn.getAttribute('data-ck-upsell-id');
        if (!uid) return;

        // Toggle Kaching Checkbox
        toggleKachingUpsell(uid);

        // Toggle CK-Karte visuell
        const isNowSelected = !btn.classList.contains('is-selected');
        btn.classList.toggle('is-selected', isNowSelected);
        btn.setAttribute('aria-pressed', isNowSelected ? 'true' : 'false');

        // Gesamtpreis im Buy-Button aktualisieren
        updateTotalPrice(sectionId);
      });
    });
  };

  /* ── Initialisierung ─────────────────────────────────────────────────── */

  const initBridge = (buyButtonRoot) => {
    if (!buyButtonRoot || buyButtonRoot.dataset.ckKachingBridgeInit === 'true') return;
    buyButtonRoot.dataset.ckKachingBridgeInit = 'true';

    const sectionId = buyButtonRoot.getAttribute('data-ck-section-id');

    const getVariantsRoot = () =>
      sectionId
        ? document.querySelector(
            `[data-ck-custom-variants][data-ck-section-id="${CSS.escape(sectionId)}"]`
          )
        : document.querySelector('[data-ck-custom-variants]');

    /* ─ 1. Karten-Klick: Kaching Deal-Bar sofort synchronisieren ───── */

    const variantsRoot = getVariantsRoot();
    if (variantsRoot) {
      variantsRoot.addEventListener('click', (e) => {
        const card = e.target.closest('[data-ck-variant-card]');
        if (!card) return;
        const dealBarId = card.getAttribute('data-ck-deal-bar-id');
        if (dealBarId) {
          selectKachingDealBar(dealBarId);

          // Upsell-Grid fuer die neue Deal-Bar aktualisieren
          // Kleiner Delay damit Kaching die DOM-Updates abschliessen kann
          setTimeout(() => {
            buildUpsellGrid(dealBarId, sectionId);
            // Gesamtpreis zuruecksetzen (nur Hauptprodukt, kein Upsell)
            updateTotalPrice(sectionId);
          }, 150);
        }
      });
    }

    /* ─ 2. Kaufbutton-Klick: Sicherheits-Sync + Klick durchlassen ── */

    const ckSubmitBtn = buyButtonRoot.querySelector(
      'button.product-form__submit.ck-buy-button__submit'
    );
    if (!ckSubmitBtn) return;

    ckSubmitBtn.addEventListener(
      'click',
      (e) => {
        const kachingBlock = getKachingBlock();
        if (!kachingBlock) {
          // Kein Kaching-Widget vorhanden – normalen Shopify-Submit durchlassen
          return;
        }

        // Sicherheits-Sync: Stelle sicher, dass die richtige Deal-Bar aktiv ist
        const vRoot = getVariantsRoot();
        const dealBarId = getActiveDealBarId(vRoot);
        if (dealBarId) {
          selectKachingDealBar(dealBarId);
        }

        // WICHTIG: Klick NICHT blockieren (kein preventDefault/stopImmediatePropagation)
        // Kaching hat die hidden inputs im Formular gesetzt.
        // Kaching uebernimmt auch die Upsell-Produkte automatisch.
      },
      true
    );

    /* ─ 3. Initiales Upsell-Grid bauen ──────────────────────────────── */

    const initialDealBarId = getActiveDealBarId(variantsRoot);
    if (initialDealBarId) {
      buildUpsellGrid(initialDealBarId, sectionId);
    }
  };

  /* ── Boot ─────────────────────────────────────────────────────────── */

  const boot = () => {
    document.querySelectorAll('[data-ck-buy-button]').forEach(initBridge);
  };

  const startWithDelay = () => setTimeout(boot, 600);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWithDelay);
  } else {
    startWithDelay();
  }
})();
